import { toQueryString } from ".";
import {
	BorrowTokenPayload,
	BorrowTokenResponse,
	DateRangeParams,
	EnergyAccountResponse,
	GuestBillResponse,
	InitiateFriendPaymentRequestPayload,
	InitiateGuestPayBillPayload,
	InitiateGuestPayBillResponse,
	InitiateFriendPaymentRequestResponse,
	InitiatePostpaidNotificationCardPayload,
	InitiatePostpaidNotificationWalletPayload,
	InitiateVendingCardPayload,
	InitiateVendingPayload,
	MeterConsumptionGraphResponse,
	VerifyCustomerResponse,
	VerifyMeterNumberPayload,
} from "../model/charge";
import { BillHistoryResponse } from "../pages/customer/energy-management/models/bill-history-response";
import { LastPurchaseResponse } from "../pages/customer/energy-management/models/last-purchase-response";
import {
	CustomerSolarMeterConfigurationResponse,
	PauseSubscriptionRequest,
	PayAsYouGoSubscriptionResult,
	SolarConsumptionGraphParams,
	SolarConsumptionGraphResponse,
	SolarConsumptionParams,
	SolarConsumptionTotalResponse,
} from "../model/solar-consumption";
import { CustomerEnergyAccountRequest, CustomerMeterNumber, EnergyProfile, VerifyNinRequest } from "../model/energy-profile";
import { VendingToken } from "../model/transaction";
import { apiSlice } from "../stores/base/api-slice";
import { AddNewMeterNumberRequest, AedcRelatedAccount, GeneratePrepaidStatementRequest, GeneratePostpaidStatementRequest, PrepaidStatementParams, PostpaidStatementParams, StatementOfAccountData, PrepaidTransactionData, SaveAsDraftMeterRequest } from "../model/vending/vending-interfaces";

const serviceBaseurl = "/vending";

const vendingEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getEnergyAccount: builder.query<EnergyProfile, void>({
			providesTags: ["CustomerDashboard", "EnergyAccount"],
			query: () => ({
				url: `${serviceBaseurl}/customer-energy-account`,
				method: "GET",
			}),
		}),
		getCustomerMeterNumbers: builder.query<CustomerMeterNumber[], void>({
			providesTags: ["EnergyAccount"],
			query: () => ({
				url: `${serviceBaseurl}/customer-energy-account/meters`,
				method: "GET",
			}),
		}),
		unlinkCustomerMeter: builder.mutation<void, string>({
			invalidatesTags: ["EnergyAccount"],
			query: meterId => ({
				url: `${serviceBaseurl}/customer-energy-account/unlink-meter`,
				method: "DELETE",
				params: { id: meterId },
			}),
		}),
		unlinkMeterByUtilityAdmin: builder.mutation<void, { customerId: string; meterId: string; reason: number }>({
			invalidatesTags: ["EnergyAccount"],
			query: ({ customerId, meterId, reason }) => ({
				url: `${serviceBaseurl}/customer-energy-account/unlink-meter/utility-admin`,
				method: "DELETE",
				params: { CustomerId: customerId, MeterId: meterId, Reason: reason },
			}),
		}),
		updateEnergyAccount: builder.mutation<any, CustomerEnergyAccountRequest>({
			invalidatesTags: ["CustomerDashboard", "EnergyAccount"],
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account`,
				method: "PUT",
				body,
			}),
		}),
		createEnergyAccount: builder.mutation<any, CustomerEnergyAccountRequest>({
			invalidatesTags: ["CustomerDashboard", "EnergyAccount"],
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account`,
				method: "POST",
				body,
			}),
		}),
		reVend: builder.mutation<VendingToken, { vendingDetailsId: string }>({
			invalidatesTags: ["Transactions", "Transaction"],
			query: vendingDetailsId => ({
				url: `${serviceBaseurl}/prepaid/retry-token-vend`,
				method: "POST",
				body: vendingDetailsId,
			}),
		}),
		notifyBilling: builder.mutation<void, { billingDetailId: string }>({
			invalidatesTags: ["Transactions", "Transaction"],
			query: body => ({
				url: `${serviceBaseurl}/postpaid/notify-billing`,
				method: "POST",
				body,
			}),
		}),
		verifyPostpaidMeter: builder.query<VerifyCustomerResponse, VerifyMeterNumberPayload>({
			query: params => ({
				url: `${serviceBaseurl}/postpaid/verify-customer`,
				method: "GET",
				params,
			}),
		}),
		verifyPrepaidMeter: builder.query<VerifyCustomerResponse, VerifyMeterNumberPayload>({
			query: params => ({
				url: `${serviceBaseurl}/prepaid/verify`,
				method: "GET",
				params,
			}),
		}),
		verifyVendingToken: builder.mutation<VendingToken, string>({
			invalidatesTags: ["Transactions", "Transaction"],
			query: vendingDetailsId => ({
				url: `${serviceBaseurl}/prepaid/verify-token-status` + toQueryString({ VendingDetailsId: vendingDetailsId }),
				method: "GET",
			}),
		}),
		getMeterConsumption: builder.query<EnergyAccountResponse, DateRangeParams>({
			query: params => ({
				url: `${serviceBaseurl}/meter-consumption`,
				method: "GET",
				params,
			}),
			providesTags: ["CustomerDashboard", "MeterConsumption"],
		}),
		compareLastMonthConsumption: builder.query<{ percentage: number }, void>({
			query: () => ({
				url: `${serviceBaseurl}/meter-consumption/compare-last-month`,
				method: "GET",
			}),
		}),
		getMeterConsumptionGraph: builder.query<MeterConsumptionGraphResponse, DateRangeParams>({
			query: params => ({
				url: `${serviceBaseurl}/meter-consumption/graph`,
				method: "GET",
				params: {
					From: params.from,
					To: params.to,
					IsMonthly: params.isMonthly,
				},
			}),
			providesTags: ["CustomerDashboard", "ConsumptionGraph"],
		}),
		getMeterConsumptionAnalytics: builder.query<
			{
				highestUsageDay: string;
				consumedThanLastWeek: number;
				moreOnWeekends: boolean;
			},
			void
		>({
			query: () => ({
				url: `${serviceBaseurl}/meter-consumption/get-card-data`,
				method: "GET",
			}),
		}),
		initiatePrepaidPayment: builder.mutation<any, InitiateVendingCardPayload>({
			// for card payment
			query: body => ({
				url: `${serviceBaseurl}/prepaid/initiate-payment`,
				method: "POST",
				body,
			}),
		}),
		initiatePrepaidPayWithWallet: builder.mutation<any, InitiateVendingPayload>({
			// for wallet
			query: body => ({
				url: `${serviceBaseurl}/prepaid/initiate-pay-vend-with-wallet`,
				method: "POST",
				body,
			}),
		}),
		initiatePostpaidPayment: builder.mutation<string, InitiateVendingCardPayload>({
			// for card payment
			query: body => ({
				url: `${serviceBaseurl}/postpaid/initiate-pay-bill`,
				method: "POST",
				body,
			}),
		}),
		initiatePostpaidPayWithWallet: builder.mutation<{ refference: string }, InitiateVendingPayload>({
			// for wallet
			query: body => ({
				url: `${serviceBaseurl}/postpaid/initiate-pay-bill-with-wallet`,
				method: "POST",
				body,
			}),
		}),
		initiatePostpaidNotificationPayWithCard: builder.mutation<{ returnUrl: string; reference: string }, InitiatePostpaidNotificationCardPayload>({
			query: body => ({
				url: `${serviceBaseurl}/postpaid/notifications/pay-with-card`,
				method: "POST",
				body,
			}),
		}),
		initiatePostpaidNotificationPayWithWallet: builder.mutation<{ refference: string }, InitiatePostpaidNotificationWalletPayload>({
			query: body => ({
				url: `${serviceBaseurl}/postpaid/notifications/pay-with-wallet`,
				method: "POST",
				body,
			}),
		}),
		borrowToken: builder.mutation<BorrowTokenResponse, BorrowTokenPayload>({
			query: body => ({
				url: `${serviceBaseurl}/prepaid/borrow-token`,
				method: "POST",
				body,
			}),
		}),
		initiatePrepaidFriendPaymentRequest: builder.mutation<InitiateFriendPaymentRequestResponse, InitiateFriendPaymentRequestPayload>({
			query: body => ({
				url: `${serviceBaseurl}/prepaid/initiate-payment-friend-request`,
				method: "POST",
				body,
			}),
		}),
		initiatePostpaidFriendPaymentRequest: builder.mutation<InitiateFriendPaymentRequestResponse, InitiateFriendPaymentRequestPayload>({
			query: body => ({
				url: `${serviceBaseurl}/postpaid/initiate-pay-bill-friend-request`,
				method: "POST",
				body,
			}),
		}),
		getGuestBill: builder.query<GuestBillResponse, { token: string; utilityName: string }>({
			query: params => ({
				url: `${serviceBaseurl}/postpaid/guest/bill`,
				method: "GET",
				params,
			}),
		}),
		initiateGuestPayBill: builder.mutation<InitiateGuestPayBillResponse, InitiateGuestPayBillPayload>({
			query: body => ({
				url: `${serviceBaseurl}/postpaid/guest/initiate-pay-bill`,
				method: "POST",
				body,
			}),
		}),
		updatePreferRemoteTokenLoad: builder.mutation<any, { preferRemoteTokenLoad: boolean }>({
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account/prefer-remote-token-load`,
				method: "PUT",
				body,
			}),
		}),
		getCustomerSolarMeters: builder.query<CustomerSolarMeterConfigurationResponse[], void>({
			query: () => ({
				url: `${serviceBaseurl}/pay-as-you-go/customer-solar-meter`,
				method: "GET",
			}),
			providesTags: ["CustomerDashboard"],
		}),
		getSolarConsumptionTotal: builder.query<SolarConsumptionTotalResponse, SolarConsumptionParams>({
			query: params => ({
				url: `${serviceBaseurl}/solar-consumption/total`,
				method: "GET",
				params,
			}),
			providesTags: ["CustomerDashboard"],
		}),
		getSolarConsumptionGraph: builder.query<SolarConsumptionGraphResponse, SolarConsumptionGraphParams>({
			query: params => ({
				url: `${serviceBaseurl}/solar-consumption/graph`,
				method: "GET",
				params,
			}),
			providesTags: ["CustomerDashboard"],
		}),
		pauseSolarSubscription: builder.mutation<void, PauseSubscriptionRequest>({
			query: body => ({
				url: `${serviceBaseurl}/pay-as-you-go/pause`,
				method: "POST",
				body,
			}),
		}),
		resumeSolarSubscription: builder.mutation<void, PauseSubscriptionRequest>({
			query: body => ({
				url: `${serviceBaseurl}/pay-as-you-go/user-reactive`,
				method: "POST",
				body,
			}),
		}),
		getPayAsYouGoSubscription: builder.query<PayAsYouGoSubscriptionResult, void>({
			query: () => ({
				url: `pay-as-you-go/customer`,
				method: "GET",
			}),
			providesTags: ["CustomerDashboard"],
		}),
		downloadPayAsYouGoBill: builder.query<string, string>({
			query: billId => ({
				url: `/pdf/download-pay-as-you-go-subscription-bill/${billId}`,
				method: "GET",
			}),
		}),
		getBillHistory: builder.query<BillHistoryResponse, void>({
			query: () => ({
				url: `${serviceBaseurl}/postpaid/bill-history`,
				method: "GET",
			}),
		}),
		getLastPurchase: builder.query<LastPurchaseResponse, void>({
			query: () => ({
				url: `${serviceBaseurl}/purchase/last-purchase`,
				method: "GET",
			}),
		}),

        saveCustomerEnergyAccountAsDraft: builder.mutation<any, CustomerEnergyAccountRequest>({
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account/save-as-draft`,
				method: "POST",
				body,
			}),
		}),

        verifyNin: builder.mutation<any, VerifyNinRequest>({
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account/verify-nin`,
				method: "POST",
				body,
			}),
		}),

		initiateNinPhoneVerification: builder.mutation<string, void>({
			query: () => ({
				url: `${serviceBaseurl}/customer-energy-account/verify-nin-phone/initiate`,
				method: "POST",
			}),
		}),

		verifyNinPhoneOtp: builder.mutation<any, { otp: string }>({
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account/verify-nin-phone/otp`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["EnergyAccount"],
		}),

		getAllCustomerMeterNumbers: builder.query<CustomerMeterNumber[], void>({
			query: () => ({
				url: `${serviceBaseurl}/customer-energy-account/meters`,
				method: "GET",
			}),
			providesTags: ["EnergyAccount"],
		}),
		activateCustomerMeter: builder.mutation<void, string>({
			query: meterId => ({
				url: `${serviceBaseurl}/customer-energy-account/meter/active`,
				method: "PUT",
				body: { meterId },
			}),
			invalidatesTags: ["EnergyAccount"],
		}),
		addMeterNumberToEnergyProfile: builder.mutation<void, AddNewMeterNumberRequest>({
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account/meter`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["EnergyAccount"],
		}),
		saveCustomerMeterAsDraft: builder.mutation<void, SaveAsDraftMeterRequest>({
			query: body => ({
				url: `${serviceBaseurl}/customer-energy-account/meter/save-as-draft`,
				method: "POST",
				body,
			}),
		}),
		getAedcRelatedAccounts: builder.query<AedcRelatedAccount[], void>({
			query: () => ({
				url: `${serviceBaseurl}/aedc/related-accounts`,
				method: "GET",
			}),
			transformResponse: (response: { accounts: AedcRelatedAccount[] }) => response.accounts ?? [],
		}),
		generatePrepaidStatementOfAccountReport: builder.mutation<any, GeneratePrepaidStatementRequest>({
			query: body => ({
				url: `${serviceBaseurl}/account-activities/statement-of-account/prepaid`,
				method: "POST",
				body,
			}),
		}),
		generatePostpaidStatementOfAccountReport: builder.mutation<any, GeneratePostpaidStatementRequest>({
			query: body => ({
				url: `${serviceBaseurl}/account-activities/statement-of-account/postpaid`,
				method: "POST",
				body,
			}),
		}),
		getPrepaidStatementOfAccount: builder.query<StatementOfAccountData, PrepaidStatementParams>({
			query: params => ({
				url: `${serviceBaseurl}/account-activities/statement-of-account/prepaid`,
				method: "GET",
				params,
			}),
		}),
		getPostpaidStatementOfAccount: builder.query<StatementOfAccountData, PostpaidStatementParams>({
			query: params => ({
				url: `${serviceBaseurl}/account-activities/statement-of-account/postpaid`,
				method: "GET",
				params,
			}),
		}),
		getPrepaidTransactionByReferenceId: builder.query<PrepaidTransactionData, string>({
			query: referenceId => ({
				url: `${serviceBaseurl}/account-activities/prepaid-transactions/${referenceId}`,
				method: "GET",
			}),
		}),
	}),
});

export const {
	useGetMeterConsumptionAnalyticsQuery,
	useCreateEnergyAccountMutation,
	useUpdateEnergyAccountMutation,
	useVerifyVendingTokenMutation,
	useReVendMutation,
	useNotifyBillingMutation,
	useGetEnergyAccountQuery,
	useLazyGetEnergyAccountQuery,
	useGetCustomerMeterNumbersQuery,
	useUnlinkCustomerMeterMutation,
	useUnlinkMeterByUtilityAdminMutation,
	useGetMeterConsumptionGraphQuery,
	useGetMeterConsumptionQuery,
	useLazyVerifyPostpaidMeterQuery,
	useLazyVerifyPrepaidMeterQuery,
	useInitiatePostpaidPayWithWalletMutation,
	useInitiatePostpaidPaymentMutation,
	useInitiatePostpaidNotificationPayWithCardMutation,
	useInitiatePostpaidNotificationPayWithWalletMutation,
	useInitiatePrepaidPayWithWalletMutation,
	useInitiatePrepaidPaymentMutation,
	useCompareLastMonthConsumptionQuery,
	useBorrowTokenMutation,
	useInitiatePrepaidFriendPaymentRequestMutation,
	useInitiatePostpaidFriendPaymentRequestMutation,
	useGetGuestBillQuery,
	useInitiateGuestPayBillMutation,
	useUpdatePreferRemoteTokenLoadMutation,
	useGetCustomerSolarMetersQuery,
	useGetSolarConsumptionTotalQuery,
	useGetSolarConsumptionGraphQuery,
	usePauseSolarSubscriptionMutation,
	useResumeSolarSubscriptionMutation,
	useGetPayAsYouGoSubscriptionQuery,
	useLazyDownloadPayAsYouGoBillQuery,
	useLazyGetBillHistoryQuery,
	useGetLastPurchaseQuery,
	useLazyGetLastPurchaseQuery,
	useSaveCustomerEnergyAccountAsDraftMutation,
	useVerifyNinMutation,
	useInitiateNinPhoneVerificationMutation,
	useVerifyNinPhoneOtpMutation,
	useGetAllCustomerMeterNumbersQuery,
	useLazyGetAllCustomerMeterNumbersQuery,
	useActivateCustomerMeterMutation,
	useAddMeterNumberToEnergyProfileMutation,
	useSaveCustomerMeterAsDraftMutation,
	useGetAedcRelatedAccountsQuery,
	useGeneratePrepaidStatementOfAccountReportMutation,
	useGeneratePostpaidStatementOfAccountReportMutation,
	useGetPrepaidStatementOfAccountQuery,
	useGetPostpaidStatementOfAccountQuery,
	useGetPrepaidTransactionByReferenceIdQuery,
} = vendingEndpoints;
